import { ApolloClient } from '@apollo/client'
import { GET_RECEIPT, GET_RECEIPT_BY_PAYMENT } from '../graphql/mutations'
import { Receipt } from '../types/order'

export type ReceiptType = 'CUSTOMER' | 'MERCHANT'

export class ReceiptService {
  constructor(private apolloClient: ApolloClient<any>) {}

  async getReceiptByOrder(orderId: number | string, receiptType: ReceiptType = 'CUSTOMER'): Promise<Receipt> {
    try {
      const response = await this.apolloClient.query({
        query: GET_RECEIPT,
        variables: { 
          order_id: Number(orderId),
          receipt_type: receiptType
        },
        fetchPolicy: 'cache-first'
      })

      if (!response.data?.receipt) {
        throw new Error('Receipt not found for the specified order')
      }

      return response.data.receipt
    } catch (error) {
      throw new Error('Failed to fetch receipt. Please try again.')
    }
  }

  async getReceiptByPayment(paymentId: number | string, receiptType: ReceiptType = 'CUSTOMER'): Promise<Receipt> {
    try {
      const response = await this.apolloClient.query({
        query: GET_RECEIPT_BY_PAYMENT,
        variables: { 
          payment_id: Number(paymentId),
          receipt_type: receiptType
        },
        fetchPolicy: 'cache-first'
      })

      if (!response.data?.receiptByPayment) {
        throw new Error('Receipt not found for the specified payment')
      }

      return response.data.receiptByPayment
    } catch (error) {
      throw new Error('Failed to fetch receipt. Please try again.')
    }
  }

  // Helper method to get receipt by order ID (primary method) - defaults to customer receipt
  async getReceipt(orderId: number | string, receiptType: ReceiptType = 'CUSTOMER'): Promise<Receipt> {
    return this.getReceiptByOrder(orderId, receiptType)
  }

  // Convenience methods for specific receipt types
  async getCustomerReceiptByOrder(orderId: number | string): Promise<Receipt> {
    return this.getReceiptByOrder(orderId, 'CUSTOMER')
  }

  async getMerchantReceiptByOrder(orderId: number | string): Promise<Receipt> {
    return this.getReceiptByOrder(orderId, 'MERCHANT')
  }

  async getCustomerReceiptByPayment(paymentId: number | string): Promise<Receipt> {
    return this.getReceiptByPayment(paymentId, 'CUSTOMER')
  }

  /**
   * WARNING: Merchant receipts are NOT available for refund payments.
   * This method will fail if called with a refund payment ID.
   * Use getCustomerReceiptByPayment for refund payments instead.
   */
  async getMerchantReceiptByPayment(paymentId: number | string): Promise<Receipt> {
    return this.getReceiptByPayment(paymentId, 'MERCHANT')
  }
}
